import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import psychometricsData from './data/psychometrics.json';
import { calculateResults, calculateScores, safeEvaluate, getPsychometrics, toPascalCase } from './utils/calculations';
import { deepClean, validateData } from './utils/runtimeValidation';
import { normalizeSessionQuestions } from './utils/normalizeSessionQuestions';

let questionsData: any;
try {
  questionsData = require('./data/questions.json');
} catch (e) {
  if (typeof window !== 'undefined') {
    const msg = 'CRITICAL: Файл questions.json не найден или невалиден. Приложение не может работать.';
    window.CRITICAL_STARTUP_ERROR = msg;
    const root = document.getElementById('root');
    if (root) {
      root.innerHTML = `<div style="color:red;padding:32px;font-size:20px;font-family:monospace;">${msg}</div>`;
    }
  }
  throw e;
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// TypeScript: Extend Window interface for custom properties
declare global {
  interface Window {
    _psyco_debug?: any;
    _psyco_debug_log?: any[];
    resultsLog?: any; // must match type 'any'
    migrationLog?: any; // must match type 'any'
    logPsycoDebug?: ((...args: any[]) => void) | undefined;
    PLAYWRIGHT_E2E?: boolean;
    psychometrics?: any; // must match type 'any'
    calculateResults?: typeof calculateResults;
    Psyco?: { calculateResults?: typeof calculateResults };
    CRITICAL_STARTUP_ERROR?: string;
    questions?: any[]; // must match type 'any'
    calculateScores?: typeof calculateScores;
    safeEvaluate?: typeof safeEvaluate;
    deepClean?: typeof deepClean;
    validateData?: typeof validateData;
    toPascalCase?: typeof toPascalCase;
    normalizeSessionQuestions?: typeof normalizeSessionQuestions;
    getPsychometrics?: typeof getPsychometrics;
  }
}

if (typeof window !== 'undefined') {
  // --- Критическая проверка наличия файлов ---
  try {
    if (!psychometricsData || typeof psychometricsData !== 'object' || Object.keys(psychometricsData).length === 0) {
      throw new Error('CRITICAL: Файл psychometrics.json отсутствует или пуст. Приложение не может работать.');
    }
    if (!questionsData || typeof questionsData !== 'object' || !Array.isArray(questionsData.questions) || questionsData.questions.length === 0) {
      throw new Error('CRITICAL: Файл questions.json отсутствует или не содержит вопросов. Приложение не может работать.');
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    // Показываем ошибку на странице (если возможно)
    const root = document.getElementById('root');
    if (root) {
      root.innerHTML = `<div style="color:red;padding:32px;font-size:20px;font-family:monospace;">${e instanceof Error ? e.message : e}</div>`;
    }
    window.CRITICAL_STARTUP_ERROR = e instanceof Error ? e.message : e;
    throw e;
  }
  // Централизованное логирование
  window._psyco_debug_log ??= [];
  window.resultsLog ??= [];
  window.migrationLog ??= [];
  window.psychometrics = psychometricsData;
  window.questions = questionsData.questions;
  window.calculateResults = calculateResults;
  window.calculateScores = calculateScores;
  window.safeEvaluate = safeEvaluate;
  window.deepClean = deepClean;
  window.validateData = validateData;
  window.toPascalCase = toPascalCase;
  window.normalizeSessionQuestions = normalizeSessionQuestions;
  window.getPsychometrics = getPsychometrics;
  window.Psyco = window.Psyco || {};
  window.Psyco.calculateResults = calculateResults;
  // --- Добавляю экспорт calculateScores ---
  window.calculateScores = calculateScores;
  window.logPsycoDebug = function(obj) {
    window._psyco_debug = obj;
    (window._psyco_debug_log ??= []).push(JSON.parse(JSON.stringify(obj)));
    if (obj && obj.step) {
      const step = obj.step.toLowerCase();
      if (step.includes('migration')) {
        (window.migrationLog ??= []).push(JSON.parse(JSON.stringify(obj)));
      }
      if (step.includes('result')) {
        (window.resultsLog ??= []).push(JSON.parse(JSON.stringify(obj)));
      }
    }
    if (window.PLAYWRIGHT_E2E) {
      // В e2e-режиме — всегда log+error
      console.error('[PSYCO_DEBUG]', obj);
    } else {
      console.log('[PSYCO_DEBUG]', obj);
    }
  };
  window.onerror = function(message, source, lineno, colno, error) {
    const errObj = {
      step: 'window.onerror',
      message,
      source,
      lineno,
      colno,
      error: error && error.stack ? error.stack : error
    };
    window.logPsycoDebug?.(errObj);
    return false;
  };
  window.addEventListener('unhandledrejection', function(e) {
    const rejObj = {
      step: 'unhandledrejection',
      reason: e.reason,
      event: e
    };
    window.logPsycoDebug?.(rejObj);
  });
}
